import { createI18n } from 'vue-i18n';
import type { App } from 'vue';
import type { I18nOptions } from 'vue-i18n';
import { setHtmlPageLang } from '@mts-fe/utils';
import { changeDevExtremeLocale } from '@mts/locales/dx';
import { getLocaleStore } from '@/store';
import { loadLocaleMsg, openLocaleWorker } from './helper';

type I18nInstance = ReturnType<typeof createI18n>;

// 将 i18n 实例挂到 globalThis，防止 HMR 重新执行模块时实例丢失
const I18N_GLOBAL_KEY = '__MTS_I18N_INSTANCE__';
export let i18n: I18nInstance = (globalThis as any)[I18N_GLOBAL_KEY];

async function createI18nOptions(): Promise<I18nOptions> {
  const localeStore = getLocaleStore();
  const locale = localeStore.getLocale;

  const defaultLocal = await import(`./lang/${locale}.ts`);
  const message = defaultLocal.default?.message ?? {};
  const cachedLang = localStorage.getItem('mts-language');
  if (cachedLang) {
    try {
      Object.assign(message, JSON.parse(cachedLang));
      openLocaleWorker();
    } catch (error) {
      console.log(error);
    }
  } else {
    await loadLocaleMsg(locale);
  }

  setHtmlPageLang(locale);

  await changeDevExtremeLocale(locale);

  return {
    legacy: false,
    locale,
    fallbackLocale: localeStore.localInfo.fallback,
    messages: {
      [locale]: message,
    },
    availableLocales: localeStore.localInfo.availableLocales,
    sync: true, //If you don't want to inherit locale from global scope, you need to set sync of i18n component option to false.
    silentTranslationWarn: true, // true - warning off
    missingWarn: false,
    fallbackWarn: false,
    silentFallbackWarn: true,
  };
}

// setup i18n instance with glob
export async function setupI18n(app: App) {
  const options = await createI18nOptions();
  i18n = createI18n(options);
  (globalThis as any)[I18N_GLOBAL_KEY] = i18n;
  app.use(i18n);
}
